import React, { useMemo } from 'react';
import { Row, Col, Alert, Typography } from 'antd';
import type { SimulationDetail, SimulationReportingTableRecord } from '../../store/simulation/types';
import { useSimulationReport, useReportFilterParams } from '../../hooks/simulation/useSimulationReport';
import { SimulationReportFilters } from './SimulationReportFilters';
import { SimulationCard } from './SimulationCard';
import { SimulationReportingTable } from './SimulationReportingTable';
import type { CompactPieDatum } from '../../components/reporting/DonutChartCompact';
import { DonutChartCompact } from '../../components/reporting/DonutChartCompact';
import { partnerTimeInTransitDataTypeMap } from './utils';

const { Title } = Typography;

export interface SimulationDetailTabProps {
  simulation: SimulationDetail;
}

export const SimulationTNTReportTab = ({ simulation }: SimulationDetailTabProps) => {
  const isComplete = useMemo(() => simulation.simulationStatus === 'completed', [simulation]);
  const tntMapToUse = partnerTimeInTransitDataTypeMap;
  const queryParams = useReportFilterParams();

  const { reportData, loading } = useSimulationReport(simulation, 'time-in-transit-report', queryParams);
  const { timeInTransitCounts } = reportData || {};

  const countSummaryNormalized = Object.values(timeInTransitCounts || [])?.reduce<
    Record<string, SimulationReportingTableRecord>
  >((prev, row: any) => {
    const dim = tntMapToUse[row.timeInTransitDataType] || row.timeInTransitDataType;
    if (!prev[dim]) {
      prev[dim] = {
        key: dim,
        dimension: dim,
        simulationValue: 0,
        historicalValue: 0,
      };
    }

    prev[dim].simulationValue += row.simulationCount;
    prev[dim].historicalValue += row.historicalCount;

    return prev;
  }, {});

  const countSummaryData: SimulationReportingTableRecord[] = Object.values(countSummaryNormalized).sort(
    (a: SimulationReportingTableRecord, b: SimulationReportingTableRecord) => b.simulationValue - a.simulationValue,
  );

  const simulationTotal = countSummaryData.reduce((acc, row) => acc + row.simulationValue, 0);
  const historicalTotal = countSummaryData.reduce((acc, row) => {
    return row.key ? acc + (row.historicalValue || 0) : acc;
  }, 0);

  const simulationCountPieData: CompactPieDatum[] = countSummaryData
    ?.map((row: SimulationReportingTableRecord) => {
      return {
        id: row.key,
        label: row.dimension,
        key: row.key,
        value: row.simulationValue,
        percent: (row.simulationValue * 100) / simulationTotal / 100,
      };
    })
    .sort((a: CompactPieDatum, b: CompactPieDatum) => b.value - a.value);

  const historicalCountPieData: CompactPieDatum[] = countSummaryData
    ?.map((row: SimulationReportingTableRecord) => {
      return {
        id: row.key,
        label: row.dimension,
        key: row.key,
        value: row.historicalValue || 0,
        percent: ((row.historicalValue || 0) * 100) / historicalTotal / 100,
      };
    })
    .sort((a: CompactPieDatum, b: CompactPieDatum) => b.value - a.value);

  const includeHistorical = historicalTotal > 0;

  return (
    <Row gutter={16}>
      <Col span={24}>
        {!isComplete && (
          <Alert
            type={'warning'}
            showIcon={true}
            description={'The Time in Transit report will be available once the simulation has completed execution.'}
            message={'Simulation Incomplete'}
          />
        )}
        {isComplete && (
          <>
            <SimulationReportFilters simulation={simulation} />
            <SimulationCard
              loading={loading}
              heading={'Time in Transit Source Type Counts'}
              bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
            >
              <Row>
                {includeHistorical && (
                  <Col span={12}>
                    <div style={{ padding: 10 }}>
                      <Title type={'secondary'} level={5} style={{ textAlign: 'center', marginBottom: -10 }}>
                        Historical Shipments
                      </Title>
                      <DonutChartCompact data={historicalCountPieData} />
                    </div>
                  </Col>
                )}
                <Col span={includeHistorical ? 12 : 24}>
                  <div style={{ padding: 10 }}>
                    <Title type={'secondary'} level={5} style={{ textAlign: 'center', marginBottom: -10 }}>
                      Simulated Shipments
                    </Title>
                    <DonutChartCompact data={simulationCountPieData} />
                  </div>
                </Col>
              </Row>
              <SimulationReportingTable
                simulation={simulation}
                dimensionTitle={'Carrier Service Method'}
                valueType={'number'}
                invertColors={true}
                colorDifference={true}
                showDifference={false}
                showPercentageOfTotal={true}
                records={countSummaryData}
                includeHistorical={includeHistorical}
              />
            </SimulationCard>
          </>
        )}
      </Col>
    </Row>
  );
};
